<script setup lang="ts">
import { computed, onBeforeMount, provide, ref } from 'vue';
import { useRoute } from 'vue-router';

import { navigateToUrl } from 'single-spa';

import {
  TSButton,
  TSButtonSearch,
  TSDialogConfirm,
  useLoadingStore,
  useToast,
} from 'tsv2-library';
import { FetchResponse } from 'tsv2-library/dist/src/components/v2/DataTable/DataTable.vue';

import LinkedAssetDialog from '@/components/common/Dialog/LinkedAssetDialog.vue';
import TransactionDetail from '@/components/common/TransactionDetail.vue';
import BorrowConfirmTable from './BorrowConfirmTable.vue';

import BorrowingServices from '@/services/borrowing.service';

import { getApproverList } from '@/utils/getData.utils';

import { PutBorrowingHandoverConfirmationDTO } from '@/dto/borrowingTransaction.dto';

import useTableStore from '@/store/table.store';

import { ApprovalTransaction } from '@/types/approval.type';
import {
  BorrowingTransactionDetail,
  BorrowingTransactionRequest,
} from '@/types/borrowing.type';
import { BorrowingRequestFetchParams } from '@/types/params.type';

onBeforeMount(async () => {
  try {
    setTrxId();

    const isTokenValid = route?.query?.token ?? transactionId.value;

    if (isTokenValid) {
      currentToken.value = (route?.query?.token as string) ?? '';

      if (currentToken.value) {
        await verifyToken();
      }

      await getTransactionDetail();
      await getApproverList(transactionId, setLoading, approvalList, toast);

      incrementTableKey();
    } else {
      throw new Error('Invalid Token');
    }
  } catch (error) {
    if (error instanceof Error) {
      if (error.message === 'Invalid Token') {
        if (window.Cypress) return;
        navigateToUrl('/not-found');
      }
    }
  }
});

const toast = useToast();
const route = useRoute();
const { setLoading } = useLoadingStore();
const { incrementTableKey } = useTableStore();

const currentToken = ref<string>();
const searchModel = ref<string>();
const transactionId = ref<string>();
const fetchedData = ref<BorrowingTransactionRequest[]>([]);
const selectedData = ref<BorrowingTransactionRequest[]>([]);
const totalRecords = ref<number>(0);
const transactionDetail = ref<BorrowingTransactionDetail>();
const approvalList = ref<ApprovalTransaction[]>([]);
const isFirstFetch = ref<boolean>(true);

const showCancelConfirmation = ref<boolean>(false);
const showAbortConfirmation = ref<boolean>(false);
const showConfirmConfirmation = ref<boolean>(false);
const showLinkedAssetConfirm = ref<boolean>(false);

const setTrxId = (): void => {
  transactionId.value =
    sessionStorage.getItem('assetBorrowTransactionId') ?? '';
};

const verifyToken = async (): Promise<void> => {
  if (!currentToken.value) return;
  try {
    setLoading(true);

    const { data } = await BorrowingServices.putBorrowingVerifyToken(
      currentToken.value,
    );

    transactionId.value = data.data._id;
  } catch {
    throw new Error('Invalid Token');
  } finally {
    setLoading(false);
  }
};

/**
 * Get the transaction detail from the server.
 */
const getTransactionDetail = async (): Promise<void> => {
  if (!transactionId.value) return;
  try {
    setLoading(true);

    const { data } = await BorrowingServices.getBorrowingTransactionDetail(
      transactionId.value,
    );

    transactionDetail.value = data.data;
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to fetch transaction detail',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};

/**
 * Get borrow transaction request all data.
 */
const getBorrowingRequestData = async (
  query: Omit<BorrowingRequestFetchParams, 'prelist'>,
): Promise<FetchResponse | undefined> => {
  if (!transactionId.value) return;
  try {
    query.children = JSON.stringify([transactionDetail.value?.borrower._id]);

    const { data } = await BorrowingServices.getBorrowingTransactionRequest(
      transactionId.value,
      {
        ...query,
        status: JSON.stringify(['Waiting for Handover']),
      },
    );

    fetchedData.value = data.data.data;

    if (isFirstFetch.value) {
      selectedData.value = fetchedData.value;
      isFirstFetch.value = false;
    }

    totalRecords.value = data.data.totalRecords;

    return data.data;
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to fetch transaction request.',
      severity: 'error',
    });
  }
};

const handleCancel = (): void => {
  if (window.Cypress) return;
  navigateToUrl('/my-asset/task/asset-borrowing');
};

/**
 * Handle the abortion of the transaction.
 */
const handleAbort = async (): Promise<void> => {
  if (!transactionId.value) return;
  try {
    setLoading(true);

    await BorrowingServices.putCancelBorrowingTransaction([
      transactionId.value,
    ]);

    toast.add({
      message: 'Success, handover has been aborted.',
      severity: 'success',
    });

    if (window.Cypress) return;
    navigateToUrl('/my-asset/task/asset-borrowing');
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to abort handover.',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};

const handleContinue = async (): Promise<void> => {
  if (!currentToken.value) return;
  try {
    setLoading(true);

    const dataDTO: PutBorrowingHandoverConfirmationDTO = {
      token: currentToken.value,
      request: selectedData.value.map((datum) => datum._id),
    };

    await BorrowingServices.putBorrowingHandoverConfirmation(dataDTO);

    toast.add({
      message: 'Success, asset handover has been confirmed.',
      severity: 'success',
    });

    await getTransactionDetail();

    incrementTableKey();
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to confirm asset handover.',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};

const handleClose = (): void => {
  if (window.Cypress) return;
  navigateToUrl('/my-asset/task/asset-borrowing');
};

const isSelectedLinkedAsset = computed(() =>
  selectedData.value.some(
    (datum) => datum.children && datum.children.length > 0,
  ),
);

const isLinkedAsset = computed(() =>
  fetchedData.value.some(
    (datum) => datum.children && datum.children.length > 0,
  ),
);

provide('approvalList', approvalList);
provide('transactionId', transactionId);
provide('transactionDetail', transactionDetail);
provide('isLinkedAsset', isLinkedAsset);
</script>
<template>
  <LinkedAssetDialog
    v-if="isSelectedLinkedAsset"
    v-model:visible="showLinkedAssetConfirm"
    :data="selectedData"
    @confirm="handleContinue"
    mode="Confirmation"
  />
  <TSDialogConfirm
    v-model:visible="showCancelConfirmation"
    @confirm="handleCancel"
    close-after-confirm
    confirm-label="Continue"
    data-ts-section="cancel-confirm-dialog"
    header="Cancel Asset Handover"
    severity="danger"
  >
    <template #body>
      <span>
        You can go back to this page but we will not be saving your latest
        action.
      </span>
      <span>Are you sure you want to continue?</span>
    </template>
  </TSDialogConfirm>
  <TSDialogConfirm
    v-model:visible="showAbortConfirmation"
    @confirm="handleAbort"
    close-after-confirm
    confirm-label="Abort"
    data-ts-section="abort-confirm-dialog"
    header="Abort Asset Handover"
    severity="danger"
  >
    <template #body>
      <span>
        You cannot go back to this action and this request will be removed.
      </span>
      <span>Are you sure you want to abort it?</span>
    </template>
  </TSDialogConfirm>
  <TSDialogConfirm
    v-model:visible="showConfirmConfirmation"
    @confirm="
      isSelectedLinkedAsset ? (showLinkedAssetConfirm = true) : handleContinue()
    "
    close-after-confirm
    confirm-label="Continue"
    data-ts-section="confirm-confirm-dialog"
    header="Asset Verification"
    severity="success"
  >
    <template #body>
      <span>
        {{ selectedData.length }} asset(s) are confirmed.
        {{ totalRecords - selectedData.length }} unconfirmed asset(s) will be
        remove from this handover process.
      </span>
      <span>Are you sure want to continue?</span>
    </template>
  </TSDialogConfirm>
  <TransactionDetail type="Detail" />
  <div
    class="flex justify-between items-center"
    data-ts-section="borrow-confirm-header"
  >
    <template v-if="!transactionDetail?.confirmationEmailConfirmed">
      <span class="font-semibold text-xs leading-4 tracking-[0.24px]">
        Selected asset(s) will be handed over. Unselect asset(s) if you want to
        cancel borrowing
      </span>
      <TSButtonSearch
        @search="searchModel = $event"
        data-ts-section="borrow-confirm-search-button"
      />
    </template>
    <template v-else>
      <div class="font-semibold text-xs leading-4 tracking-[0.24px]">
        <span class="text-success-600">Handover confirmation is suceeded!</span>
        <span>
          . Contact borrow manager ({{ transactionDetail.manager.fullName }}) to
          take your asset(s).
        </span>
      </div>
    </template>
  </div>
  <BorrowConfirmTable
    v-model:selected-data="selectedData"
    :fetch-function="getBorrowingRequestData"
    :is-confirmed="transactionDetail?.confirmationEmailConfirmed ?? false"
    :search="searchModel"
  />
  <div
    class="flex flex-col gap-1 items-end"
    data-ts-section="borrow-confirm-footer"
  >
    <div class="flex gap-1 items-center">
      <template v-if="!transactionDetail?.confirmationEmailConfirmed">
        <TSButton
          @click="showCancelConfirmation = true"
          data-ts-section="cancel-button"
          label="Cancel"
          severity="dark"
          text
        />
        <TSButton
          @click="showAbortConfirmation = true"
          data-ts-section="abort-button"
          label="Abort"
          outlined
          severity="danger"
        />
        <TSButton
          @click="
            selectedData.length
              ? (showConfirmConfirmation = true)
              : (showAbortConfirmation = true)
          "
          data-ts-section="confirm-button"
          label="Confirm"
          severity="success"
        />
      </template>
      <template v-else>
        <TSButton
          @click="handleClose"
          data-ts-section="close-button"
          label="Close"
          outlined
        />
      </template>
    </div>
  </div>
</template>
